/* eslint-disable no-restricted-globals */

import * as Sentry from '@sentry/vue'
import PricingApi from '@/api/pricing'
import PromoApi from '@/api/promo'
import utils from '@/util'
import router from '@/router'
import InvoiceApi from '@/api/invoice'
import mixpanelUtils from '@/utils/mixpanel.utils'
import {
  MONTHLY_YEARLY_UPGRADE_PLAN_MAP,
  DEFAULT_MONTHLY_TO_YEARLY_DISCOUNT_PERCENTAGE,
} from '@/constant/monthlyToYearlyUpgrade'
import { ROUTE_NAMES } from '@/constant/routes'
import { DIRECT_BULK_PURCHASE_STATUS } from '@/constant/directBulkPurchase'

const DEFAULT_TOAST_ERROR_MESSAGE = 'Something went wrong, please check your connection or contact us for support'
const EXTENDED_CYCLE_UNAVAILABLE_MESSAGE = 'This plan option is not available right now. Please refresh the page and try again, or contact support.'

const getTrackingData = () => {
  try {
    return mixpanelUtils.getProperties()
  } catch (e) {
    Sentry.captureException(e)
    return undefined
  }
}

const createCheckoutSessionAction = async ({ commit, dispatch }, payload) => {
  try {
    commit('isCreatingCheckoutSession', true)

    // Use the v4 bulk checkout endpoint: it accepts a Stripe lookupKey
    // (variant-aware) and resolves it to a plan whose canonical `.type` is
    // used by the multi-license coupon lookup. The legacy V2 endpoint keyed
    // the coupon off the raw lookupKey, so Unleash price-variant lookupKeys
    // (e.g. `pro-yearly-vietnam-50pct-20260507`) silently lost the bulk
    // discount.
    const res = await PricingApi.createBulkCheckoutSessionV4({
      lookupKey: payload.plan,
      quantity: payload.quantity,
      origin: window.location.href,
      trackingData: getTrackingData(),
      ...(payload.priceVariantStandard ? { priceVariantStandard: payload.priceVariantStandard } : {}),
      ...(payload.priceVariantEdu ? { priceVariantEdu: payload.priceVariantEdu } : {}),
      ...(payload.pricingSurface ? { pricingSurface: payload.pricingSurface } : {}),
    })
    if (res.sessionUrl) {
      window.location.href = res.sessionUrl
    }
  } catch (e) {
    if (e.body && e.body.message) {
      commit('createCheckoutSessionErrorMutation', e.body.message)
    }
    dispatch('hookBulkPricingAction', {
      planType: payload.plan,
      numOfLicenses: payload.quantity,
      email: payload.email,
      requirement: 'This was initiated because of failure during checkout session creation.',
    })
    throw e
  } finally {
    commit('isCreatingCheckoutSession', false)
  }
}
const createCheckoutSessionForPersonPlan = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const {
      plan: planType, promotionCode, query, pricingSurface,
      priceVariantStandard, priceVariantEdu, currency,
    } = payload
    commit('loadingPlan', planType)

    const data = await PricingApi.createCheckoutSessionForPersonPlan({
      planType,
      promotionCode,
      query,
      trackingData: getTrackingData(),
      ...(currency ? { currency } : {}),
      // Forwarded from aha-pricing → register → stripe-transition. The BE
      // controller tags the Mixpanel user profile with
      // `pricing_standard_variant` / `pricing_edu_variant` and stamps both
      // fields into Stripe metadata so the `invoice.payment_succeeded` →
      // Mixpanel `Make payment` event carries attribution. Logged-in buyers
      // on /pricing skip this path entirely (they call v4 directly); this is
      // the anonymous-first parity fix. Two scoped fields prevent collision
      // between the standard pricing A/B test and the edu pricing A/B test.
      ...(pricingSurface ? { pricingSurface } : {}),
      ...(priceVariantStandard ? { priceVariantStandard } : {}),
      ...(priceVariantEdu ? { priceVariantEdu } : {}),
    })
    if (data && data.sessionUrl) {
      window.location.href = data.sessionUrl
    }
  } catch (e) {
    Sentry.captureException(e)
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    return e
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}
const createCustomerPortalUpgradeFlowAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const data = await PricingApi.createCustomerPortalUpgradeFlowV2(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
      commit('loadingPlan', null)
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
    Sentry.captureException(e)
    return e
  }
}
const createCustomerPortalUpgradeFlowV3Action = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    const data = await PricingApi.createCustomerPortalUpgradeFlowV3(payload)
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      commit('loadingPlan', null)
      window.location.href = data.url
    }
    if (data?.message) {
      utils.showToastSuccess(data.message)
    }
  } catch (e) {
    const errorMessage = e?.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    Sentry.captureException(e)
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}
const createCustomerPortalAction = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    const data = await PricingApi.createCustomerPortal({
      pathname: window.location.pathname,
      isUpdatingPlan: payload ? payload.isUpdatingPlan : false,
    })
    commit('isCreatingCustomerPortal', false)
    if (data && data.url) {
      window.location.href = data.url
    }
  } catch (e) {
    const errorMessage = e.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(
      errorMessage,
    )
    commit('isCreatingCustomerPortal', false)
    Sentry.captureException(e)
  }
}
const hookBulkPricingAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookBulkPricing(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookBulkPricingErrorMutation', e)
  }
}
const getPromotionCode = async ({ commit }, payload) => {
  const { promotionCode } = await PromoApi.checkPromotion({
    promo: payload,
  })
  commit('setPromotionCode', promotionCode)
}

// Mint a per-user, single-redemption promo code for a URL-driven campaign,
// AFTER the user is authenticated (the only moment such a code can exist).
// Used by the stripe-transition hop so landing-site buyers get the campaign
// discount attached to Stripe checkout without exposing a public code.
// Degrades gracefully: returns null on any failure (ineligible / campaign off /
// network error) so checkout still proceeds at full price rather than blocking
// the upgrade.
const fetchCampaignPromoCodeAction = async (_, campaign) => {
  try {
    const { promotionCode } = await PricingApi.generatePromoCode({ campaign })
    return promotionCode || null
  } catch (e) {
    Sentry.captureException(e)
    return null
  }
}

const setIsValidPromoCodeAction = async ({ commit }, payload) => {
  commit('setIsValidPromoCode', payload)
}
const updateRemoveCardPurposeAction = ({ commit }, payload) => {
  commit('setRemoveCardPurpose', payload)
}

const requestAdditionalLicensePopupAction = async ({ commit }, payload) => {
  commit('isShowingRequestAdditionalLicensePopup', payload)
}

const goToBulkLicensePageAction = () => {
  router.push({
    name: 'UserPlan',
    hash: '#bulk-pricing',
  })
}
const trackNewPurchaseAction = (
  { state }, payload,
) => {
  const { currentUser, plan } = state

  if (payload.query) {
    setTimeout(async () => {
      const res = await InvoiceApi.getInvoices()

      if (res.invoices && res.invoices.length === 0) {
        return
      }
      const invoice = res.invoices[0]

      const { source, type } = payload.query

      let coupon = ''
      if (invoice.coupon) {
        coupon = invoice.coupon.name
      }
      let discount = 0
      if (invoice.lines && invoice.lines.length > 0) {
        discount = invoice.lines[invoice.lines.length - 1].discountAmountRaw
      }
      const payment_method = invoice.paymentMethod.brand === 'visa' ? 'Stripe > Cards' : 'Stripe > iDEAL with SEPA'
      const params = {
        currency: invoice.currency,
        ahaslides_user_id: currentUser.id,
        transaction_id: invoice.invoiceId,
        value: invoice.amountPaidRaw,
        coupon,
        tax: 0,
        item_name: plan.name,
        discount,
        payment_method,
        item_category: plan.group,
        item_category2: plan.billingCycle,
        item_category3: type,
        item_variant: source === 'landing_site' ? 'Landing site' : 'Presenter app',
      }
      utils.trackGA4AndMixpanel('purchase', {
        ...params,
      })
      router.replace({ query: null })
    }, 1000)
  }
}

const hookCustomRequestAction = async ({ commit }, payload) => {
  try {
    return PricingApi.hookCustomRequest(payload)
  } catch (e) {
    Sentry.captureException(e)
    commit('hookCustomRequestError', e)
  }
}

const submitEnterpriseInquiriesFormAction = async (_, payload) => {
  try {
    return PricingApi.submitEnterpriseInquiriesForm({ ...payload, source: getTrackingData() })
  } catch (e) {
    Sentry.captureException(e)
  }
}

const getMonthlyToYearlyUpgradePreviewAction = async ({ commit, state, getters }) => {
  try {
    const currentPlan = state.currentUser.plan
    const upgradePlan = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!upgradePlan) {
      return
    }

    // If the user is on a discount arm, use that arm's discountPercentage so the
    // backend returns the matching promo code (GOYEARLY{N}) from /yearly-upgrade-preview.
    // The percentage comes from the Unleash variant payload when set, falling back to
    // parsing the variant name (e.g. `discount-20pct` → 20). Control / flag-off uses
    // the permanent winning #4240 deal percentage.
    const discountVariant = getters['featureToggles/monthlyToYearlyDiscountVariant']
    const discountPercentage = discountVariant?.discountPercentage
      ?? DEFAULT_MONTHLY_TO_YEARLY_DISCOUNT_PERCENTAGE

    const res = await PricingApi.getMonthlyToYearlyUpgradePreview({
      plan: upgradePlan,
      discountPercentage,
    })
    commit('setMonthlyToYearlyUpgradePreview', res)
  } catch (e) {
    Sentry.captureException(e)
    commit('setMonthlyToYearlyUpgradePreview', null)
  }
}

const upgradeFromMonthlyToYearlyAction = async ({
  getters, commit, dispatch, state,
}) => {
  try {
    if (state.isUpgradingFromMonthlyToYearly) {
      return
    }
    commit('setUpgradingFromMonthlyToYearly', true)
    const currentPlan = state.currentUser.plan
    const planToUpgrade = MONTHLY_YEARLY_UPGRADE_PLAN_MAP[currentPlan]
    if (!currentPlan || !planToUpgrade) {
      utils.showToastError(
        'The plan is not eligible for upgrade to yearly plan',
      )
      return
    }
    const payload = {
      plan: planToUpgrade,
    }
    // The promotionCode comes from /yearly-upgrade-preview (backend-derived, keyed off
    // discountPercentage). Control / flag-off and BOO-11 discount arms both flow through
    // this path — no frontend override needed. Never hardcode a coupon code here.
    if (getters.monthlyToYearlyUpgradePromotionCode) {
      payload.promotionCode = getters.monthlyToYearlyUpgradePromotionCode
    }
    await dispatch('createCustomerPortalUpgradeFlowAction', payload)
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(
      DEFAULT_TOAST_ERROR_MESSAGE,
    )
  } finally {
    commit('setUpgradingFromMonthlyToYearly', false)
  }
}

const setShowMonthlyToYearlyPromoBannerAction = ({ commit }, payload) => {
  commit('setIsShowMonthlyToYearlyPromoBanner', payload)
}

// Split a canonical plan key into the plan level + billing cycle used to look up
// the variant price in the lineup. The level is the key with the trailing cycle
// (and any date suffix) removed, so multi-segment levels survive:
//   'pro-yearly'                 → { level: 'pro',       cycle: 'yearly'   }
//   'pro-monthly-24122019'       → { level: 'pro',       cycle: 'monthly' }
//   'edu-small-yearly'           → { level: 'edu-small', cycle: 'yearly'  }
//   'pro-biyearly-20260708'      → { level: 'pro',       cycle: 'biyearly' }
//   'pro-lifetime'               → { level: 'pro',       cycle: 'lifetime' }
// BOO-149: cycle detection must check 'biyearly' (2-year) and 'lifetime' BEFORE
// 'yearly' — 'biyearly' contains 'yearly' as a substring, so a naive
// `.includes('yearly')` first would misparse a biyearly key as cycle 'yearly'
// (then fail to strip it, since the key actually contains '-biyearly-' not
// '-yearly-'), silently falling back to the base yearly price at checkout.
const parsePlanLevelAndCycle = (planKey) => {
  if (!planKey || typeof planKey !== 'string') return { level: null, cycle: null }
  const lower = planKey.toLowerCase()
  let cycle = null
  if (lower.includes('biyearly')) {
    cycle = 'biyearly'
  } else if (lower.includes('lifetime')) {
    cycle = 'lifetime'
  } else if (lower.includes('yearly')) {
    cycle = 'yearly'
  } else if (lower.includes('monthly')) {
    cycle = 'monthly'
  }
  // Everything before the cycle token is the lineup plan key (e.g. 'pro', 'edu-small').
  const level = cycle ? lower.split(`-${cycle}`)[0] : lower
  return { level, cycle }
}

/**
 * biyearly (2-year) and lifetime cycles are served ONLY by the variant-aware v4
 * lookupKey checkout. The legacy planType checkout can neither resolve those keys
 * nor mint a one-off lifetime (it is subscription-mode), so routing them there
 * returns a cryptic "Unknown planType" 400. Block the request when the chosen
 * extended cycle has no usable variant price — the lineup does not carry it, or the
 * release flag gating the v4 path is off — so the caller can surface a clear message.
 *
 * @param {string|null} cycle              parsed billing cycle
 * @param {string|null} lookupKey          variant Stripe lookupKey for plan+cycle
 * @param {boolean} priceAlignmentEnabled  release-price-alignment-fix toggle
 * @returns {boolean}
 */
const isExtendedCycleUnavailable = (cycle, lookupKey, priceAlignmentEnabled) => {
  const isExtendedCycle = cycle === 'biyearly' || cycle === 'lifetime'
  return isExtendedCycle && !(priceAlignmentEnabled && lookupKey)
}

const upgradePlanAction = async ({ state, dispatch, rootGetters }, payload) => {
  if (!state.isAuthenticated) {
    router.replace({
      name: ROUTE_NAMES.LOGIN,
      query: {
        redirect: window.location.href,
      },
    })
    return
  }

  const { level, cycle } = parsePlanLevelAndCycle(payload.plan)
  const lookupKey = rootGetters['pricingLineup/variantPriceLookupKey'](level, cycle)

  if (isExtendedCycleUnavailable(cycle, lookupKey, rootGetters['featureToggles/canUsePriceAlignmentFix'])) {
    utils.showToastError(EXTENDED_CYCLE_UNAVAILABLE_MESSAGE)
    return
  }

  const isFreeUser = state.currentUser.plan === 'free'

  // Price alignment: when a price experiment is running for this viewer the shared
  // pricing lineup exposes a variant Stripe lookupKey for this plan+cycle. Forward
  // it to the variant-aware v4 endpoints so the presenter charges the SAME price
  // the pricing page advertised — for BOTH funnels, matching aha-pricing's
  // PricingButton (free → personal-plan checkout v4; paying subscriber → portal
  // upgrade v4).
  //
  // The `release-price-alignment-fix` toggle is the OUTER gate: the whole aligned
  // path lives in the `if` block and the untouched legacy funnels in the `else`.
  // Rollout/cleanup is then a one-step delete of the else branch (no interleaved
  // conditionals to untangle). When the flag is off, behaviour is byte-for-byte
  // identical to today.
  if (rootGetters['featureToggles/canUsePriceAlignmentFix']) {
    if (isFreeUser) {
      if (lookupKey) {
        await dispatch('createCheckoutSessionForPersonalPlanV4Action', { ...payload, lookupKey })
      } else {
        // No variant price (lineup not loaded / no experiment / unpriced tier) →
        // canonical new-subscription checkout, identical to today.
        await dispatch('createCheckoutSessionForPersonPlan', payload)
      }
    } else if (lookupKey) {
      await dispatch('createCustomerPortalUpgradeFlowV4Action', { ...payload, lookupKey })
    } else {
      // Paying subscriber with no variant price → canonical portal upgrade.
      await dispatch('createCustomerPortalUpgradeFlowV3Action', payload)
    }
    return
  }

  // Flag off → canonical legacy funnels, behaviour byte-for-byte unchanged.
  if (isFreeUser) {
    await dispatch('createCheckoutSessionForPersonPlan', payload)
  } else {
    await dispatch('createCustomerPortalUpgradeFlowV3Action', payload)
  }
}

// Variant-aware checkout for a free user (new subscription). Mirrors
// createCheckoutSessionForPersonPlan but sends the Stripe `lookupKey` instead of a
// canonical planType, so the price matches the running pricing-page experiment.
const createCheckoutSessionForPersonalPlanV4Action = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)
    const data = await PricingApi.createCheckoutSessionForPersonalPlanV4({
      lookupKey: payload.lookupKey,
      query: payload.query,
      promotionCode: payload.promotionCode,
      // Mirror the v1 path: derive tracking data here so Mixpanel attribution is
      // never dropped just because the caller didn't pre-populate it.
      trackingData: payload.trackingData || getTrackingData(),
      ...(payload.currency ? { currency: payload.currency } : {}),
      ...(payload.pricingSurface ? { pricingSurface: payload.pricingSurface } : {}),
      ...(payload.priceVariantStandard ? { priceVariantStandard: payload.priceVariantStandard } : {}),
      ...(payload.priceVariantEdu ? { priceVariantEdu: payload.priceVariantEdu } : {}),
    })
    if (data && data.sessionUrl) {
      window.location.href = data.sessionUrl
    } else if (data?.message) {
      // e.g. "You already have a subscription" (200 with no sessionUrl)
      utils.showToastSuccess(data.message)
    }
  } catch (e) {
    Sentry.captureException(e)
    utils.showToastError(e?.body?.message || DEFAULT_TOAST_ERROR_MESSAGE)
    return e
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
  return undefined
}

// Variant-aware portal upgrade for an existing paying subscriber. Mirrors
// createCustomerPortalUpgradeFlowV3Action but sends the Stripe `lookupKey` so the
// proration preview the subscriber confirms uses the running pricing-page price.
const createCustomerPortalUpgradeFlowV4Action = async ({ commit }, payload) => {
  try {
    commit('isCreatingCustomerPortal', true)
    commit('loadingPlan', payload.plan)

    // payload already carries the variant `lookupKey` (merged by upgradePlanAction);
    // forward it wholesale exactly like the V3 action so attribution/feature fields
    // pass through unchanged. The v4 endpoint reads `lookupKey` and resolves the
    // price variant-aware, then delegates to the same V3 portal-upgrade service.
    const data = await PricingApi.createCustomerPortalUpgradeFlowV4(payload)
    if (data && data.url) {
      window.location.href = data.url
    } else if (data?.message) {
      // e.g. "You already have this plan" (200 with no url)
      utils.showToastSuccess(data.message)
    }
  } catch (e) {
    const errorMessage = e?.body?.message || DEFAULT_TOAST_ERROR_MESSAGE
    utils.showToastError(errorMessage)
    Sentry.captureException(e)
  } finally {
    commit('isCreatingCustomerPortal', false)
    commit('loadingPlan', null)
  }
}

// AHA-38492: Simplified to handle direct bulk purchase via single API call
const bulkPurchaseRedirectAction = async ({ commit, getters }, payload) => {
  // Not logged in → redirect to signup
  if (payload.user.id === null) {
    router.replace({
      name: ROUTE_NAMES.LOGIN,
      query: {
        redirect: window.location.href,
      },
    })
    return
  }

  if (!getters['featureToggles/canUseDirectBulkPurchaseLinks']) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.INVALID_PARAMS)
    return
  }

  // Validate basic params
  if (!payload || !payload.plan || !payload.quantity) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.INVALID_PARAMS)
    return
  }

  try {
    // Call unified API - backend handles all logic
    const response = await PricingApi.handleDirectBulkPurchase({
      plan: payload.plan,
      quantity: payload.quantity,
    })

    if (response.canCheckout && response.sessionUrl) {
      // User can checkout - redirect to Stripe
      window.location.href = response.sessionUrl
    } else {
      // User needs sales assistance - show confirmation message
      commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.ORDER_RECEIVED)
    }
  } catch (error) {
    commit('setDirectBulkPurchaseStatus', DIRECT_BULK_PURCHASE_STATUS.PURCHASE_ERROR)
  }
}

export default {
  createCheckoutSessionAction,
  createCheckoutSessionForPersonPlan,
  hookBulkPricingAction,
  updateRemoveCardPurposeAction,
  createCustomerPortalAction,
  createCustomerPortalUpgradeFlowAction,
  requestAdditionalLicensePopupAction,
  goToBulkLicensePageAction,
  trackNewPurchaseAction,
  hookCustomRequestAction,
  getPromotionCode,
  fetchCampaignPromoCodeAction,
  setIsValidPromoCodeAction,
  submitEnterpriseInquiriesFormAction,
  upgradeFromMonthlyToYearlyAction,
  getMonthlyToYearlyUpgradePreviewAction,
  setShowMonthlyToYearlyPromoBannerAction,
  upgradePlanAction,
  createCheckoutSessionForPersonalPlanV4Action,
  createCustomerPortalUpgradeFlowV3Action,
  createCustomerPortalUpgradeFlowV4Action,
  bulkPurchaseRedirectAction,
}
